import { ReactNode, useId } from 'react';

import {
  FormGroup,
  FormGroupProps,
  FormHelpText,
  FormInput,
  FormInputIcon,
  FormInputIconProps,
  FormInputProps,
} from '@spotify-internal/encore-web';

export type FormTextFieldProps = Omit<FormInputProps, 'id' | 'error'> & {
  label: string;
  helperText?: ReactNode;
  error?: string | null;
  invalid?: boolean;
  iconLeading?: FormInputIconProps['iconLeading'];
  iconTrailing?: FormInputIconProps['iconTrailing'];
  indicator?: FormGroupProps['indicator'];
  popover?: FormGroupProps['popover'];
  'data-testid'?: string;
};

export const FormTextField = ({
  label,
  helperText,
  error,
  invalid = false,
  iconLeading,
  iconTrailing,
  indicator,
  popover,
  name,
  required,
  size = 'small',
  'data-testid': dataTestId = name,
  ...inputProps
}: FormTextFieldProps) => {
  const inputId = useId();
  const helpTextId = useId();
  const helpText = error ?? helperText;
  const hasError = invalid || Boolean(error);

  return (
    <FormGroup
      label={label}
      labelFor={inputId}
      indicator={indicator}
      popover={popover}
      data-testid={dataTestId}
    >
      <FormInputIcon iconLeading={iconLeading} iconTrailing={iconTrailing}>
        <FormInput
          {...inputProps}
          id={inputId}
          name={name}
          size={size}
          error={hasError}
          required={required}
          aria-describedby={helpText ? helpTextId : undefined}
        />
      </FormInputIcon>
      {helpText && (
        <FormHelpText id={helpTextId} error={hasError}>
          {helpText}
        </FormHelpText>
      )}
    </FormGroup>
  );
};
